/**
 * @graphql cc
 */
import { graphql } from 'react-relay';
import { useState } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useMutation,
	useApolloClient,
} from '@apollo/react-hooks';
import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import gql from 'graphql-tag';

import { expValEquals } from '@confluence/feature-experiments';
import { useWriteFragmentInterop } from '@confluence/apollo-relay-interop/entry-points/useWriteFragmentInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import type {
	useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation as useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$data,
	useHomeViewsUserPreferencesHomeViewsUserPreferencesMutationVariables as useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$variables,
} from './__types__/useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation.ts';
import { HomeViewsUserPreferencesQuery } from './HomeViewsUserPreferencesQuery.graphql.ts';
import type { HomeViewsUserPreferencesQuery as HomeViewsUserPreferencesQueryType } from './__types__/HomeViewsUserPreferencesQuery.ts';
import { PagesDisplayPersistenceOption } from './__types__/HomeViewsUserPreferencesQuery.ts';

type UseHomeViewsUserPreference = (defaultPreference?: PagesDisplayPersistenceOption) => {
	loading: boolean;
	queryError?: ApolloErrorInterop;
	mutationError?: ApolloErrorInterop;
	appearance: PagesDisplayPersistenceOption;
	updateHomeViewsUserPreference: (appearance: PagesDisplayPersistenceOption) => void;
};

export const useLoggedInHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference = PagesDisplayPersistenceOption.CARDS,
) => {
	const { isLoggedIn } = useSessionData();
	const apolloClient = useApolloClient();
	const writeFragmentInterop = useWriteFragmentInterop<any>({
		fragmentSpec: graphql`
			fragment useHomeViewsUserPreferencesFragment on UserPreferences {
				homePagesDisplayView
			}
		`,
	});
	const {
		data,
		loading,
		error: queryError,
	} = useQuery<HomeViewsUserPreferencesQueryType>(
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Preloaded GraphQL Operation
		HomeViewsUserPreferencesQuery,
		{
			skip: !isLoggedIn,
		},
	);

	const appearance = data?.userPreferences.homePagesDisplayView || defaultPreference;
	const [homeViewsUserPreferencesMutation, { error: mutationError }] = useMutation<
		useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$data,
		useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation$variables
	>(gql`
		mutation useHomeViewsUserPreferencesHomeViewsUserPreferencesMutation(
			$appearance: PagesDisplayPersistenceOption!
		) {
			updateUserPreferences(userPreferences: { homePagesDisplayView: $appearance }) {
				homePagesDisplayView
			}
		}
	`);
	const updateHomeViewsUserPreference = (appearance: PagesDisplayPersistenceOption) => {
		if (isLoggedIn) {
			void homeViewsUserPreferencesMutation({
				variables: { appearance },
				optimisticResponse: {
					// @ts-ignore __typename does not exist on autogenerated graphql types
					__typename: 'Mutation',
					updateUserPreferences: {
						// @ts-ignore __typename does not exist on autogenerated graphql types
						__typename: 'UserPreferences',
						homePagesDisplayView: appearance,
					},
				},
			}).catch(() => {}); // Ignore the error here since it is handled in useMutation
			// Always write to the cache so the user sees the preference even if there is an error
			if (expValEquals('remove_usages_of_cache_read_write', 'isEnabled', true)) {
				// Use writeFragmentInterop instead of writeQuery
				writeFragmentInterop({
					fragmentId: '$ROOT_QUERY.userPreferences',
					data: {
						homePagesDisplayView: appearance,
						__typename: 'UserPreferences',
					},
				});
			} else {
				// eslint-disable-next-line graphql-relay-compat/no-direct-apollo-query-and-mutation-operations -- Read http://go/cfe-no-direct-apollo-query-and-mutation-operations
				apolloClient.writeQuery<HomeViewsUserPreferencesQueryType>({
					query: HomeViewsUserPreferencesQuery,
					data: {
						userPreferences: {
							// @ts-ignore __typename does not exist on autogenerated graphql types
							__typename: 'UserPreferences',
							homePagesDisplayView: appearance,
						},
					},
				});
			}
		}
	};
	return {
		loading,
		queryError,
		mutationError,
		appearance,
		updateHomeViewsUserPreference,
	};
};
export const useAnonymousHomeViewsUserPreferences: UseHomeViewsUserPreference = (
	defaultPreference: PagesDisplayPersistenceOption = PagesDisplayPersistenceOption.CARDS,
) => {
	const [appearance, setAppearance] = useState(defaultPreference);
	return {
		loading: false,
		appearance,
		updateHomeViewsUserPreference: setAppearance,
	};
};
